import { create } from 'zustand';
import { api, AdminUser } from '../lib/api';

interface UserState {
  users: AdminUser[];
  total: number;
  isLoading: boolean;
  error: string | null;
  roleFilter: string | null;
  searchQuery: string;

  setRoleFilter: (role: string | null) => void;
  setSearchQuery: (query: string) => void;
  fetchUsers: () => Promise<void>;
  approveVendor: (id: number) => Promise<void>;
  rejectVendor: (id: number) => Promise<void>;
  deleteUser: (id: number) => Promise<void>;
  updateUser: (id: number, payload: { role?: string; name?: string }) => Promise<void>;
}

export const useUserStore = create<UserState>((set, get) => ({
  users: [],
  total: 0,
  isLoading: false,
  error: null,
  roleFilter: null,
  searchQuery: '',

  setRoleFilter: (role) => set({ roleFilter: role }),
  setSearchQuery: (query) => set({ searchQuery: query }),

  fetchUsers: async () => {
    const { roleFilter, searchQuery } = get();
    set({ isLoading: true, error: null });
    try {
      const result = await api.users.list({
        role: roleFilter || undefined,
        search: searchQuery || undefined,
        limit: 200,
      });
      set({
        users: result?.users || [],
        total: result?.total || 0,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch users';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  approveVendor: async (id: number) => {
    const updated = await api.users.approve(id);
    set((state) => ({
      users: state.users.map((u) => (u.id === id ? { ...u, ...updated } : u)),
    }));
  },

  rejectVendor: async (id: number) => {
    const updated = await api.users.reject(id);
    set((state) => ({
      users: state.users.map((u) => (u.id === id ? { ...u, ...updated } : u)),
    }));
  },

  deleteUser: async (id: number) => {
    await api.users.delete(id);
    set((state) => ({
      users: state.users.filter((u) => u.id !== id),
      total: Math.max(0, state.total - 1),
    }));
  },

  updateUser: async (id: number, payload) => {
    const updated = await api.users.update(id, payload);
    set((state) => ({
      users: state.users.map((u) => (u.id === id ? { ...u, ...updated } : u)),
    }));
  },
}));

export function useUsers() {
  const store = useUserStore();
  return {
    users: store.users,
    total: store.total,
    isLoading: store.isLoading,
    error: store.error,
    roleFilter: store.roleFilter,
    searchQuery: store.searchQuery,
    setRoleFilter: store.setRoleFilter,
    setSearchQuery: store.setSearchQuery,
    fetchUsers: store.fetchUsers,
    approveVendor: store.approveVendor,
    rejectVendor: store.rejectVendor,
    deleteUser: store.deleteUser,
    updateUser: store.updateUser,
  };
}
